// Custom-service extension point: services developed IN the customer repo (typically an
// MCP server) that the deploy bundle must carry alongside the product modules.
//
// The customer repo is a real pnpm workspace; each service lives in its own
// `services/<name>` sub-workspace. `pack` adds the declared services to the compose
// output, and `admin apply` registers the ones carrying an `mcp` block — so a custom
// connector needs no manual step in the middle of an installation.
import { z } from 'zod';
import { EnvVarNameSchema } from './env-var-name.js';
/**
 * Environment values are ALWAYS references, never literals: a secret value must never
 * appear in a config file or in a bundle. The tool resolves the reference at deploy time.
 */
export const EnvVarRefSchema = z.strictObject({
    envVar: EnvVarNameSchema,
});
export const CustomServiceTypeSchema = z.enum(['mcp-server', 'generic']);
/**
 * Connector facts an `mcp-server` service must declare for automatic registration:
 * `admin apply` registers these as an MCP on the control plane, so the service needs no
 * duplicate entry in `admin.mcps`.
 *
 * The url is ALWAYS stated explicitly, never derived: it is what a desktop client dials,
 * and how the service is reachable from a client machine (host, published port, TLS) is
 * something only the distribution's author knows — a derived guess would fail at the
 * customer, as a timeout on first use.
 *
 * No `headers` field, for the same reason as in `McpDescriptorSchema`: the control plane
 * stores an MCP as name + url only, so a per-connector bearer key cannot be registered yet.
 */
export const CustomServiceMcpSchema = z.strictObject({
    /** Registered MCP name — the natural key `admin apply` matches on. */
    name: z.string().min(1),
    /** The URL the control plane registers — the address a desktop client dials. */
    url: z.url({ protocol: /^https?$/ }),
});
export const CustomServiceSchema = z
    .strictObject({
    /** Service name; also the compose service name, so keep it dns-safe. */
    name: z.string().regex(/^[a-z][a-z0-9-]*$/, 'must be lowercase letters, digits and dashes'),
    /** Workspace path inside the customer repo. */
    path: z.string().regex(/^services\/[a-z][a-z0-9-]*$/, 'must be services/<name>'),
    type: CustomServiceTypeSchema,
    port: z.number().int().min(1).max(65535),
    env: z.record(z.string(), EnvVarRefSchema).optional(),
    mcp: CustomServiceMcpSchema.optional(),
})
    .refine((value) => value.type !== 'mcp-server' || value.mcp !== undefined, {
    message: 'mcp endpoint data is required for an mcp-server service',
    path: ['mcp'],
});
//# sourceMappingURL=custom-service.js.map